<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { peopleApi } from '$lib/api/crm';
  import { apiFetch, getErrorMessage } from '$lib/api/client';
  import type { Person } from '$lib/types';

  let { verticalId, companyId, people = [], value = $bindable({}), idPrefix = 'company-contact' }: {
    verticalId?: number; companyId?: number; people?: Person[];
    value?: Record<string, unknown>; idPrefix?: string;
  } = $props();
  const client = useQueryClient();
  let mode = $state('none');
  let search = $state('');
  let contactId = $state<number | undefined>();
  let firstName = $state('');
  let lastName = $state('');
  let phone = $state('');
  let email = $state('');
  let error = $state('');
  let busy = $state(false);
  let scopeRevision = 0;
  $effect(() => {
    verticalId;
    companyId;
    scopeRevision++;
    error = '';
    mode = 'none'; contactId = undefined; search = ''; firstName = ''; lastName = ''; phone = ''; email = '';
  });
  $effect(() => {
    value = mode === 'existing' ? { contact_id: contactId } : mode === 'new'
      ? { contact: { first_name: firstName, last_name: lastName, phone, email } } : {};
  });
  const query = createQuery(() => ({
    queryKey: ['company-contact-search', verticalId, search],
    queryFn: () => peopleApi.list({ vertical_id: verticalId, q: search || undefined, per_page: 25 }),
    enabled: mode === 'existing' && verticalId !== undefined
  }));
  async function link() {
    if (busy || !companyId || mode === 'none') return;
    if (mode === 'existing' && !contactId || mode === 'new' && !firstName.trim() && !lastName.trim()) {
      error = 'Choose a contact or enter a contact name.'; return;
    }
    busy = true; error = '';
    const requestCompanyId = companyId;
    const requestVerticalId = verticalId;
    const revision = scopeRevision;
    const isCurrent = () => companyId === requestCompanyId && verticalId === requestVerticalId && scopeRevision === revision;
    try {
      await apiFetch(`/companies/${requestCompanyId}/contacts`, { method: 'POST', body: JSON.stringify(value) });
      await client.invalidateQueries({ queryKey: ['company-detail', requestCompanyId] });
      await client.invalidateQueries({ queryKey: ['people'] });
      if (isCurrent()) mode = 'none';
    } catch (err) { if (isCurrent()) error = getErrorMessage(err); } finally { busy = false; }
  }
</script>

<div class="space-y-3">
  {#if companyId}
    <h3 class="label">Contacts</h3>
    {#each people as person (person.id)}
      <div class="flex flex-wrap items-center gap-3 border-t border-[#F0F0F0] pt-3 first-of-type:border-t-0 first-of-type:pt-0">
        <div class="min-w-0 flex-1 text-sm">
          <a class="font-semibold text-navy hover:underline" href={`#/people/${person.id}`}>{`${person.first_name} ${person.last_name}`.trim() || person.email || 'Contact'}</a>
          <div class="truncate text-[12.5px] text-dark-gray">{[person.job_title, person.phone, person.email].filter(Boolean).join(' · ')}</div>
        </div>
        {#if person.phone}<a class="btn-secondary !py-1.5 text-[13px]" href={`tel:${person.phone.replace(/[^\d+]/g, '')}`}>Call</a>{/if}
      </div>
    {:else}<p class="text-sm text-dark-gray">No contacts linked yet.</p>{/each}
    {#if mode === 'none'}
      <div class="flex flex-wrap gap-2 pt-1">
        <button type="button" class="btn-secondary !py-1.5 text-[13px]" onclick={() => (mode = 'existing')}>＋ Link existing contact</button>
        <button type="button" class="btn-secondary !py-1.5 text-[13px]" onclick={() => (mode = 'new')}>＋ New contact</button>
      </div>
    {/if}
  {:else}
  <label class="label" for={`${idPrefix}-mode`}>Primary contact (optional)</label>
  <select id={`${idPrefix}-mode`} class="input" bind:value={mode}>
    <option value="none">No contact</option><option value="existing">Link existing contact</option><option value="new">Create new contact</option>
  </select>
  {/if}
  {#if mode === 'existing'}
    <label class="label" for={`${idPrefix}-search`}>Search same-team contacts</label>
    <input id={`${idPrefix}-search`} class="input" bind:value={search} />
    <label class="label" for={`${idPrefix}-existing`}>Existing contact</label>
    <select id={`${idPrefix}-existing`} class="input" bind:value={contactId} required>
      <option value={undefined}>Choose a contact</option>
      {#each query.data?.results ?? [] as person (person.id)}
        <option value={person.id}>{person.first_name} {person.last_name} — {person.phone || person.email}</option>
      {/each}
    </select>
    {#if query.isError}<p class="text-sm text-red-600">{getErrorMessage(query.error)}</p>{/if}
  {:else if mode === 'new'}
    <label class="label" for={`${idPrefix}-first`}>Contact first name</label><input id={`${idPrefix}-first`} class="input" bind:value={firstName} required />
    <label class="label" for={`${idPrefix}-last`}>Contact last name</label><input id={`${idPrefix}-last`} class="input" bind:value={lastName} />
    <label class="label" for={`${idPrefix}-phone`}>Contact phone</label><input id={`${idPrefix}-phone`} class="input" bind:value={phone} />
    <label class="label" for={`${idPrefix}-email`}>Contact email</label><input id={`${idPrefix}-email`} type="email" class="input" bind:value={email} />
    <p class="text-xs text-dark-gray">Saved on the person only, together with the company link. Sales ownership follows the team's existing policy.</p>
  {/if}
  {#if companyId && mode !== 'none'}<div class="flex gap-2"><button type="button" class="btn-primary" disabled={busy} onclick={link}>{busy ? 'Saving…' : 'Save contact link'}</button><button type="button" class="btn-secondary" disabled={busy} onclick={() => { mode = 'none'; error = ''; }}>Cancel</button></div>{/if}
  {#if error}<p class="text-sm text-red-600">{error}</p>{/if}
</div>
